import { useEffect, useState } from "react";
import ConfigUtils from "../../utils/Config";
import { useNavigate } from "react-router-dom";
import * as constantes from "../../common/Constantes";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import IAlert from "../../types/Alert";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField, Typography } from "@mui/material";
import FacturacionService from "../../services/FacturacionService";
import { CONFIGTYPES } from "../../types/Config";
import { getAnioEnCurso } from "../../utils/Numeracion";

interface FacturacionProps {
  onReady?: () => void;
}

const Facturacion: React.FC<FacturacionProps> = ({ onReady }) => {
  const history = useNavigate();
  const userId = ConfigUtils.getUserId();

  const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
  const [counter, setCounter] = useState<number>(0);
  const [showModal, setShowModal] = useState(false);
  const [seriePrefix, setSeriePrefix] = useState<string>("S");
  const [isNewYear, setIsNewYear] = useState(false);
  const [isFirstInit, setIsFirstInit] = useState(false);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);

  const currentYear = getAnioEnCurso(ConfigUtils.getDatosFacturacion()?.pais);

  const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setAlert({ open: false, severity: 'info', msg: '' });
  };

  useEffect(() => {
    setLoadingInit(true);
    const init = async () => {
      try {
        const response = await FacturacionService.getInvoiceCounter(userId);
        const data = response.data;

        if (!data) {
          // Primera vez
          setIsFirstInit(true);
          setCounter(0);
          setSeriePrefix("S");
          setShowModal(true);
        } else {
          setCounter(data.counter);
          // Solo se quita el año final: el prefijo puede contener números ("A1")
          setSeriePrefix(data.serie.replace(/\d{4}$/, ''));

          // Comprobar cambio de año
          if (!data.serie.endsWith(currentYear.toString())) {
            setIsNewYear(true);
            setShowModal(true);
            setCounter(0);
          } else {
            if (onReady) onReady();
          }
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = { method: 'Facturacion.Component.useEffect', level: 'error', message: e.message, meta: e.response };
          LogService.logError(error);
          setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingInit(false);
      }
    };
    init();
  }, [userId]);

  const handleConfirmSerie = async () => {
    try {
      setLoadingConfirm(true);
      // Validaciones
      if (!seriePrefix || seriePrefix.trim() === "") {
        setAlert({ open: true, severity: 'error', msg: 'La serie es obligatoria' });
        return;
      }
      if (!/^[A-Z0-9]+$/.test(seriePrefix)) {
        setAlert({ open: true, severity: 'error', msg: 'La serie solo admite letras y números' });
        return;
      }
      if (seriePrefix.length > 6) {
        setAlert({ open: true, severity: 'error', msg: 'El prefijo de la serie admite hasta 6 caracteres' });
        return;
      }
      if (isFirstInit && (!Number.isInteger(counter) || counter < 0 || counter > 999999)) {
        setAlert({ open: true, severity: 'error', msg: 'El número inicial debe estar entre 0 y 999999' });
        return;
      }

      const fullSerie = `${seriePrefix}${currentYear}`;
      let response;
      if (isFirstInit) {
        response = await FacturacionService.createInvoiceCounter({ userId, serie: fullSerie, counter });
      } else if (isNewYear) {
        response = await FacturacionService.updateInvoiceCounter(userId, { newSerie: fullSerie, counter: 0 });
      }

      const newCounter = response?.data;
      if (newCounter) {
        setCounter(newCounter.counter);
      }
      ConfigUtils.editConfig(CONFIGTYPES.isFacturacionReady, true);
      setShowModal(false);
      setIsFirstInit(false);
      setIsNewYear(false);
      setAlert({ open: true, severity: 'success', msg: 'Serie y numeración confirmadas correctamente' });
      if (onReady) onReady();
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = { method: 'Facturacion.Component.handleConfirmSerie', level: 'error', message: e.message, meta: e.response };
        LogService.logError(error);
        setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoadingConfirm(false);
    }
  };

  const formatInvoiceNumber = () => `${seriePrefix}${currentYear}-${(counter + 1).toString().padStart(6, "0")}`;

  return (
    <>
      <Dialog open={showModal} maxWidth="sm" fullWidth onClose={loadingConfirm ? undefined : () => setShowModal(false)}>
        {loadingInit ? <CircularProgress style={{ margin: '20px auto' }} /> : 
        <>
          <DialogTitle>{isFirstInit ? "Define el formato y la numeración inicial de las facturas" : "Cambio de año de facturación"}</DialogTitle>
          <DialogContent>
            {isFirstInit ? (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Define la serie y el número de la primera factura que se emitirá.
                  La serie no podrá modificarse posteriormente.
                  El número de factura se incrementará automáticamente con cada factura emitida.
                  Si aún no se han emitido facturas, el número inicial aparecerá como <b>0</b>.
                </Typography>
            
                <TextField
                  fullWidth
                  label="Serie (prefijo)"
                  value={seriePrefix}
                  inputProps={{ maxLength: 6, pattern: "[A-Z0-9]*" }}
                  onChange={(e) => {
                    const prefix = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    setSeriePrefix(prefix);
                  }}
                  sx={{ mb: 2 }}
                />
  
                <TextField
                  fullWidth
                  label="Número inicial"
                  type="number"
                  inputProps={{ min: 0, max: 999999, maxLength: 6 }}
                  value={counter}
                  onChange={(e) => {
                    let val = Number(e.target.value);
                    setCounter(val);
                  }}
                  sx={{ mb: 2 }}
                />
  
                <Typography variant="body1">
                  Próxima factura a emitir: <b>{formatInvoiceNumber()}</b>
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Hemos detectado que ha cambiado el año. La serie definida se mantiene, el contador se reinicia y la próxima factura emitida será la número 1.
                </Typography>
                <Typography variant="body1">
                  Próxima factura a emitir: <b>{formatInvoiceNumber()}</b>
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmSerie} variant="contained" color="primary" disabled={loadingConfirm} startIcon={loadingConfirm ? <CircularProgress size={18} /> : undefined}>Confirmar</Button>
          </DialogActions>
        </>}
      </Dialog>

      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
      </Snackbar>
    </>
  );
};

export default Facturacion;